import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Image, Modal } from 'react-native-web';
import { useParams, useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player/lazy';
import { getDRollByName, getDRollContent, approveDRollContent, denyDRollContent } from '../services/DRollService';
import Header from './Header';
import Button from './Button';
import toast, { Toaster } from 'react-hot-toast';
import blueLandscapeIcon from '../icons/blue-landscape-icon.png';
import photoIcon from "../icons/DupeIcons-14-Photo.png";
import videoIcon from "../icons/DupeIcons-12-Video.png";
import calendarIcon from "../icons/DupeIcons-23-Calendar.png";
import paymentIcon from "../icons/DupeIcons-22-Payment.png";

const EMPTY_BUCKET = { photos: [], videos: [] };
const EMPTY_CONTENT = { submitted: EMPTY_BUCKET, approved: EMPTY_BUCKET, denied: EMPTY_BUCKET };
const CONTENT_CDN_BASE = "https://d3p3fw3rutb1if.cloudfront.net/business/uploads/";

function bucketCount(bucket) {
    if (!bucket) return 0;
    return ((bucket.photos && bucket.photos.length) || 0) + ((bucket.videos && bucket.videos.length) || 0);
}

function normalizeBucket(bucket) {
    return {
        photos: (bucket && bucket.photos) || [],
        videos: (bucket && bucket.videos) || [],
    };
}

const DRoll = () => {
    const { drollSlug } = useParams();
    const navigate = useNavigate();
    
    const [droll, setDRoll] = useState(null);
    const [content, setContent] = useState(EMPTY_CONTENT);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [statusFilter, setStatusFilter] = useState('submitted');
    const [detailModal, setDetailModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const currentBucket = normalizeBucket(content[statusFilter]);

    useEffect(() => {
        if (drollSlug) {
            fetchDRoll();
        }
    }, [drollSlug]);

    useEffect(() => {
        if (droll) {
            fetchContent();
        }
    }, [droll]);

    const fetchDRoll = async () => {
        try {
            const response = await getDRollByName(drollSlug);
            if (response.status === 200 && response.data) {
                setDRoll(response.data);
            } else {
                toast.error('D-Roll not found');
                navigate('/drolls');
            }
        } catch (error) {
            toast.error('Failed to load D-Roll');
            navigate('/drolls');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchContent = async () => {
        try {
            const response = await getDRollContent(droll.id);
            if (response && response.status === 200 && response.data) {
                setContent({
                    submitted: normalizeBucket(response.data.submitted),
                    approved: normalizeBucket(response.data.approved),
                    denied: normalizeBucket(response.data.denied),
                });
            }
        } catch (error) {
            console.error('Failed to fetch d-roll content:', error);
        }
    };

    const openDetailModal = (submission) => {
        setSelectedSubmission(submission);
        setDetailModal(true);
    };

    const closeModals = () => {
        setDetailModal(false);
        setSelectedSubmission(null);
    };

    const handleApprove = async (submission) => {
        try {
            const response = await approveDRollContent(droll.id, [submission.id]);
            if (response && response.status === 200) {
                toast.success('Content approved successfully');
                fetchContent();
                closeModals();
            } else {
                toast.error('Failed to approve content');
            }
        } catch (error) {
            toast.error('Failed to approve content');
        }
    };

    const handleDeny = async (submission) => {
        try {
            const response = await denyDRollContent(droll.id, [submission.id]);
            if (response && response.status === 200) {
                toast.success('Content denied');
                fetchContent();
                closeModals();
            } else {
                toast.error('Failed to deny content');
            }
        } catch (error) {
            toast.error('Failed to deny content');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No date';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const getStatusBadgeStyle = (status) => {
        switch (status) {
            case 'approved':
                return [styles.statusBadge, styles.approvedBadge];
            case 'denied':
                return [styles.statusBadge, styles.deniedBadge];
            default:
                return [styles.statusBadge, styles.pendingBadge];
        }
    };

    const getContentTypeInfo = () => {
        if (!droll.deliverables || droll.deliverables.length === 0) {
            return 'No deliverables defined';
        }
        
        const types = [...new Set(droll.deliverables.map(d => d.contentType))];
        return types.map(type => {
            switch(type) {
                case 'photo': return 'Photos';
                case 'video': return 'Videos';
                case 'instagram_post': return 'Instagram Posts';
                case 'instagram_story': return 'Instagram Stories';
                case 'tiktok_video': return 'TikTok Videos';
                case 'youtube_video': return 'YouTube Videos';
                default: return type;
            }
        }).join(', ');
    };

    const getCompensationInfo = () => {
        const compensations = [];
        if (droll.paymentCompensation) {
            compensations.push(`$${droll.defaultPayment} payment`);
        }
        if (droll.productCompensation) {
            compensations.push('Product compensation');
        }
        if (droll.commissionCompensation) {
            compensations.push('Commission compensation');
        }
        return compensations.length > 0 ? compensations.join(', ') : 'No compensation';
    };

    if (isLoading) {
        return (
            <View style={{ height: '100vh' }}>
                <Header title="Loading..." />
                <View style={styles.loadingContainer}>
                    <Text>Loading D-Roll...</Text>
                </View>
            </View>
        );
    }

    if (!droll) {
        return (
            <View style={{ height: '100vh' }}>
                <Header title="D-Roll Not Found" />
                <View style={styles.errorContainer}>
                    <Text>D-Roll not found</Text>
                    <Button text="Back to D-Rolls" onPress={() => navigate('/drolls')} />
                </View>
            </View>
        );
    }

    const renderSubmissionCard = (submission, isVideo) => (
        <TouchableOpacity
            key={submission.id}
            style={styles.submissionCard}
            onPress={() => openDetailModal({ ...submission, content_type: isVideo ? 'VIDEO' : 'PHOTO' })}
        >
            {isVideo ? (
                <View style={styles.submissionThumbnail}>
                    <ReactPlayer
                        url={CONTENT_CDN_BASE + submission.img_preview_id}
                        width={"100%"}
                        height={"100%"}
                        style={{borderRadius: 6}}
                        muted={true}
                        playing={true}
                        loop={true}
                        playsinline={true}
                        config={{
                            file: {
                                attributes: {
                                    style: {width: '100%', height: '100%', objectFit: 'cover'},
                                },
                            },
                        }}
                    />
                </View>
            ) : (
                <Image
                    source={submission.img_preview_id ?
                        {uri: CONTENT_CDN_BASE + submission.img_preview_id} :
                        photoIcon
                    }
                    style={styles.submissionThumbnail}
                    resizeMode="cover"
                />
            )}
            <View style={styles.submissionInfo}>
                <Text style={styles.submissionCreator}>
                    {submission.user || submission.username || 'Anonymous'}
                </Text>
                <View style={getStatusBadgeStyle(statusFilter)}>
                    <Text style={styles.statusText}>
                        {statusFilter.toUpperCase()}
                    </Text>
                </View>
            </View>
        </TouchableOpacity>
    );

    return (
        <View style={{ height: '100vh' }}>
            <Header title={droll.name} />

            <ScrollView style={styles.container}>
                {/* D-Roll Info Section */}
                <View style={styles.drollInfoSection}>
                    <View style={styles.drollHeader}>
                        <Image 
                            style={styles.drollImage} 
                            source={droll.imageExists 
                                ? {uri: `https://d3p3fw3rutb1if.cloudfront.net/business/photos/${droll.id}`}
                                : blueLandscapeIcon
                            }
                            defaultSource={blueLandscapeIcon}
                            resizeMode="cover"
                        />
                        <View style={styles.drollDetails}>
                            <Text style={styles.drollName}>{droll.name}</Text>
                            <Text style={styles.drollDescription}>{droll.description}</Text>
                            {droll.preview && (
                                <Text style={styles.drollPreview}>{droll.preview}</Text>
                            )}
                            
                            <View style={styles.infoRow}>
                                <Image source={paymentIcon} style={styles.infoIcon} />
                                <Text style={styles.infoText}>{getCompensationInfo()}</Text>
                            </View>
                            
                            <View style={styles.infoRow}>
                                <Image source={photoIcon} style={styles.infoIcon} />
                                <Text style={styles.infoText}>{getContentTypeInfo()}</Text>
                            </View>
                            
                            <View style={styles.infoRow}>
                                <Image source={calendarIcon} style={styles.infoIcon} />
                                <Text style={styles.infoText}>
                                    {formatDate(droll.startDate)} - {formatDate(droll.endDate)}
                                </Text>
                            </View>
                        </View>
                        
                        <View style={styles.actionButtons}>
                            <Button 
                                text="Edit D-Roll" 
                                variant="secondary"
                                onPress={() => navigate(`/drolls/${drollSlug}/edit`)}
                                style={styles.actionButton}
                            />
                        </View>
                    </View>
                </View>

                {/* Submissions Section */}
                <View style={styles.submissionsSection}>
                    <View style={styles.submissionsHeader}>
                        <Text style={styles.sectionTitle}>Submissions</Text>
                        
                        <View style={styles.filterButtons}>
                            {['submitted', 'approved', 'denied'].map(status => (
                                <TouchableOpacity
                                    key={status}
                                    style={[
                                        styles.filterButton,
                                        statusFilter === status && styles.activeFilterButton
                                    ]}
                                    onPress={() => setStatusFilter(status)}
                                >
                                    <Text style={[
                                        styles.filterButtonText,
                                        statusFilter === status && styles.activeFilterButtonText
                                    ]}>
                                        {status.charAt(0).toUpperCase() + status.slice(1)} ({bucketCount(content[status])})
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>

                    <View style={styles.contentTypeSection}>
                        <Text style={styles.contentTypeHeader}>Photos</Text>
                        <View style={styles.submissionsGrid}>
                            {currentBucket.photos.length === 0 ? (
                                <View style={styles.emptyState}>
                                    <Text style={styles.emptyStateText}>No {statusFilter} photos yet</Text>
                                </View>
                            ) : (
                                currentBucket.photos.map(photo => renderSubmissionCard(photo, false))
                            )}
                        </View>
                    </View>

                    <View style={styles.contentTypeSection}>
                        <Text style={styles.contentTypeHeader}>Videos</Text>
                        <View style={styles.submissionsGrid}>
                            {currentBucket.videos.length === 0 ? (
                                <View style={styles.emptyState}>
                                    <Text style={styles.emptyStateText}>No {statusFilter} videos yet</Text>
                                </View>
                            ) : (
                                currentBucket.videos.map(video => renderSubmissionCard(video, true))
                            )}
                        </View>
                    </View>
                </View>
            </ScrollView>

            {/* Submission Detail Modal */}
            <Modal
                visible={detailModal}
                onRequestClose={closeModals}
                transparent={true}
                animationType="fade"
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        {selectedSubmission && (
                            <>
                                <Text style={styles.modalTitle}>Submission Details</Text>

                                {selectedSubmission.content_type === 'VIDEO' ? (
                                    <View style={[styles.modalImage, {overflow: 'hidden'}]}>
                                        <ReactPlayer
                                            url={CONTENT_CDN_BASE + (selectedSubmission.img_id || selectedSubmission.img_preview_id)}
                                            width={"100%"}
                                            height={"100%"}
                                            controls={true}
                                            playing={true}
                                            loop={true}
                                            muted={false}
                                            playsinline={true}
                                        />
                                    </View>
                                ) : (
                                    <Image
                                        source={selectedSubmission.img_preview_id ?
                                            {uri: CONTENT_CDN_BASE + selectedSubmission.img_preview_id} :
                                            blueLandscapeIcon
                                        }
                                        style={styles.modalImage}
                                        resizeMode="contain"
                                    />
                                )}

                                <View style={styles.submissionDetails}>
                                    <Text style={styles.detailLabel}>Creator:</Text>
                                    <Text style={styles.detailValue}>
                                        {selectedSubmission.user || selectedSubmission.username || 'Anonymous'}
                                    </Text>

                                    <Text style={styles.detailLabel}>Content Type:</Text>
                                    <Text style={styles.detailValue}>
                                        {selectedSubmission.content_type}
                                    </Text>
                                </View>

                                <View style={styles.modalActions}>
                                    <Button
                                        text="Close"
                                        variant="secondary"
                                        onPress={closeModals}
                                        style={styles.modalButton}
                                    />

                                    {statusFilter === 'submitted' && (
                                        <>
                                            <Button
                                                text="Approve"
                                                onPress={() => handleApprove(selectedSubmission)}
                                                style={styles.modalButton}
                                            />
                                            <Button
                                                text="Deny"
                                                variant="warning"
                                                onPress={() => handleDeny(selectedSubmission)}
                                                style={styles.modalButton}
                                            />
                                        </>
                                    )}
                                </View>
                            </>
                        )}
                    </View>
                </View>
            </Modal>

            <Toaster
                position="top-center"
                reverseOrder={false}
                gutter={8}
                toastOptions={{
                    duration: 3000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 2000,
                        theme: {
                            primary: 'green',
                            secondary: 'black',
                        },
                    },
                }}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f1f1f1',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 20,
    },
    drollInfoSection: {
        backgroundColor: 'white',
        margin: 20,
        borderRadius: 12,
        padding: 24,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 3,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    drollHeader: {
        flexDirection: 'row',
        gap: 20,
    },
    drollImage: {
        width: 120,
        height: 120,
        borderRadius: 8,
        backgroundColor: '#f3f4f6',
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    drollDetails: {
        flex: 1,
    },
    drollName: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 24,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 8,
    },
    drollDescription: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#374151',
        marginBottom: 8,
        lineHeight: 24,
    },
    drollPreview: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
        marginBottom: 16,
        fontStyle: 'italic',
    },
    infoRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
    },
    infoIcon: {
        width: 16,
        height: 16,
        marginRight: 8,
        tintColor: '#6b7280',
    },
    infoText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
    },
    actionButtons: {
        justifyContent: 'flex-start',
    },
    actionButton: {
        minWidth: 120,
    },
    submissionsSection: {
        backgroundColor: 'white',
        margin: 20,
        marginTop: 0,
        borderRadius: 12,
        padding: 24,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 3,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    submissionsHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    sectionTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 20,
        fontWeight: 'bold',
        color: '#396de0',
    },
    contentTypeSection: {
        marginTop: 16,
    },
    contentTypeHeader: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 18,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 8,
    },
    filterButtons: {
        flexDirection: 'row',
        gap: 8,
    },
    filterButton: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 6,
        backgroundColor: '#f3f4f6',
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    activeFilterButton: {
        backgroundColor: '#396de0',
        borderColor: '#396de0',
    },
    filterButtonText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
        fontWeight: '500',
    },
    activeFilterButtonText: {
        color: 'white',
    },
    submissionsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 16,
    },
    emptyState: {
        width: '100%',
        padding: 40,
        alignItems: 'center',
    },
    emptyStateText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#6b7280',
    },
    submissionCard: {
        width: 200,
        backgroundColor: '#f9fafb',
        borderRadius: 8,
        padding: 12,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    submissionThumbnail: {
        width: '100%',
        height: 120,
        borderRadius: 6,
        backgroundColor: '#e5e7eb',
        marginBottom: 8,
    },
    submissionInfo: {
        gap: 4,
    },
    submissionCreator: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        fontWeight: '600',
        color: '#374151',
    },
    statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
        alignSelf: 'flex-start',
    },
    pendingBadge: {
        backgroundColor: '#fef3c7',
    },
    approvedBadge: {
        backgroundColor: '#d1fae5',
    },
    deniedBadge: {
        backgroundColor: '#fee2e2',
    },
    statusText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 12,
        fontWeight: '600',
        color: '#374151',
    },
    paymentAmount: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        fontWeight: '600',
        color: '#396de0',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 24,
        maxWidth: 500,
        width: '100%',
        maxHeight: '80%',
    },
    modalTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 20,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 20,
        textAlign: 'center',
    },
    modalImage: {
        width: '100%',
        height: 200,
        borderRadius: 8,
        backgroundColor: '#f3f4f6',
        marginBottom: 20,
    },
    submissionDetails: {
        marginBottom: 20,
    },
    detailLabel: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        fontWeight: '600',
        color: '#374151',
        marginTop: 8,
        marginBottom: 4,
    },
    detailValue: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
    },
    paymentSection: {
        marginBottom: 20,
    },
    paymentLabel: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        fontWeight: '600',
        color: '#374151',
        marginBottom: 8,
    },
    paymentInputWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    dollarSign: {
        fontFamily: "'lato', sans-serif",
        fontSize: 18,
        fontWeight: '600',
        color: '#396de0',
        marginRight: 8,
    },
    paymentInput: {
        borderWidth: 2,
        borderColor: '#e5e7eb',
        borderRadius: 6,
        padding: 12,
        fontSize: 16,
        fontFamily: "'lato', sans-serif",
        backgroundColor: 'white',
        minWidth: 120,
        textAlign: 'center',
        outline: 'none',
    },
    modalActions: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 12,
        flexWrap: 'wrap',
    },
    modalButton: {
        minWidth: 100,
    },
});

export default DRoll; 